import React from 'react';
import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Box,
} from '@mui/material';
import type { FormikHandlers, FormikErrors, FormikTouched } from 'formik';
import { getIn } from 'formik';
import { useTranslation } from 'react-i18next';
import { CountrySelector } from '@pflegenavi/web-components';
import type {
  RecipientSchema,
  FieldDefinition,
  RecipientData,
  CountryWithTranslatedLabel,
} from '@pflegenavi/service-provider-frontend/api';

interface DynamicRecipientFormProps {
  schema: RecipientSchema;
  values: RecipientData;
  errors: FormikErrors<RecipientData>;
  touched: FormikTouched<RecipientData>;
  handleChange: FormikHandlers['handleChange'];
  handleBlur: FormikHandlers['handleBlur'];
  setFieldValue?: (field: string, value: string) => void;
  countryOptions?: CountryWithTranslatedLabel[];
  prefix?: string;
  disabled?: boolean;
}

// Type guard to check if a value is a FieldDefinition
const isFieldDefinition = (value: unknown): value is FieldDefinition => {
  return (
    typeof value === 'object' &&
    value !== null &&
    'Label' in value &&
    'Required' in value &&
    'EndUserDisplay' in value
  );
};

export const DynamicRecipientForm: React.FC<DynamicRecipientFormProps> = ({
  schema,
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  setFieldValue,
  countryOptions,
  prefix = '',
  disabled = false,
}) => {
  const { t } = useTranslation();

  // Transform country options to use ISO codes as values for MangoPay
  const mangopayCountryOptions = countryOptions?.map((country) => ({
    value: country.isoCode, // Use 2-letter ISO code for MangoPay
    label: country.label,
  }));

  // Helper function to translate field labels
  const translateFieldLabel = (label: string): string => {
    // Convert label to translation key format
    const key = label.toLowerCase().replace(/\s+/g, '-').replace(/[()]/g, '');

    const translationKey = `mangopay.recipient.fields.${key}`;
    const translated = t(translationKey);

    // If translation key not found, return original label
    return translated === translationKey ? label : translated;
  };

  // Helper function to translate nested field names
  const translateNestedFieldName = (fieldName: string): string => {
    // Skip failing translation attempts for known field types
    // Currency: EUR only
    if (fieldName === 'EUR') {
      const translationKey = 'mangopay.recipient.currency.eur';
      const translated = t(translationKey);
      return translated === translationKey ? fieldName : translated;
    }

    // Recipient types: BusinessRecipient, IndividualRecipient
    if (
      fieldName === 'BusinessRecipient' ||
      fieldName === 'IndividualRecipient'
    ) {
      const typeKey = fieldName
        .replace(/([A-Z])/g, '-$1')
        .toLowerCase()
        .replace(/^-/, '');
      const translationKey = `mangopay.recipient.types.${typeKey}`;
      const translated = t(translationKey);
      return translated === translationKey ? fieldName : translated;
    }

    // Payout methods: LocalBankTransfer, InternationalBankTransfer
    if (
      fieldName === 'LocalBankTransfer' ||
      fieldName === 'InternationalBankTransfer'
    ) {
      const typeKey = fieldName
        .replace(/([A-Z])/g, '-$1')
        .toLowerCase()
        .replace(/^-/, '');
      const translationKey = `mangopay.recipient.types.${typeKey}`;
      const translated = t(translationKey);
      return translated === translationKey ? fieldName : translated;
    }

    // For unknown field names, try fallback chain
    // First try to translate as a regular field
    const fieldKey = fieldName.toLowerCase().replace(/\s+/g, '-');

    const fieldTranslationKey = `mangopay.recipient.fields.${fieldKey}`;
    const fieldTranslated = t(fieldTranslationKey);
    if (fieldTranslated !== fieldTranslationKey) {
      return fieldTranslated;
    }

    // Special handling for recipient types and currency
    const typeKey = fieldName
      .replace(/([A-Z])/g, '-$1')
      .toLowerCase()
      .replace(/^-/, '');

    // Check if it's a recipient type
    const typeTranslationKey = `mangopay.recipient.types.${typeKey}`;
    const typeTranslated = t(typeTranslationKey);
    if (typeTranslated !== typeTranslationKey) {
      return typeTranslated;
    }

    // Check if it's a currency
    const currencyKey = fieldName.toLowerCase();
    const currencyTranslationKey = `mangopay.recipient.currency.${currencyKey}`;
    const currencyTranslated = t(currencyTranslationKey);
    if (currencyTranslated !== currencyTranslationKey) {
      return currencyTranslated;
    }

    // Default: return original if no translation found
    return fieldName;
  };
  const renderField = (
    fieldName: string,
    fieldDef: FieldDefinition | RecipientSchema | null,
    currentPath: string
  ) => {
    // Skip null or undefined values
    if (fieldDef === null || fieldDef === undefined) {
      return null;
    }

    // Check if it's a FieldDefinition using type guard
    if (isFieldDefinition(fieldDef)) {
      const field = fieldDef;

      if (field.EndUserDisplay === 'Hide') {
        return null;
      }

      const fieldPath = currentPath ? `${currentPath}.${fieldName}` : fieldName;
      const value = getIn(values, fieldPath) || '';
      const fieldTouched = getIn(touched, fieldPath);
      const error = getIn(errors, fieldPath);
      const hasError = fieldTouched && error;

      if (field.AllowedValues && field.AllowedValues.length > 0) {
        const translatedLabel = translateFieldLabel(field.Label);
        return (
          <FormControl
            key={fieldPath}
            fullWidth
            margin="normal"
            error={hasError}
          >
            <InputLabel>
              {translatedLabel}
              {field.Required && ' *'}
            </InputLabel>
            <Select
              id={fieldPath}
              name={fieldPath}
              value={value}
              onChange={handleChange}
              onBlur={handleBlur}
              label={translatedLabel + (field.Required ? ' *' : '')}
              disabled={disabled}
            >
              {field.AllowedValues.map((allowedValue) => (
                <MenuItem key={allowedValue} value={allowedValue}>
                  {allowedValue}
                </MenuItem>
              ))}
            </Select>
            {hasError && <FormHelperText>{error}</FormHelperText>}
          </FormControl>
        );
      }

      // Check if this is a country field
      const isCountryField =
        fieldName.toLowerCase() === 'country' ||
        fieldName.toLowerCase().includes('country') ||
        field.Label.toLowerCase().includes('country');

      if (
        isCountryField &&
        mangopayCountryOptions &&
        mangopayCountryOptions.length > 0 &&
        setFieldValue
      ) {
        const translatedLabel = translateFieldLabel(field.Label);
        return (
          <Box key={fieldPath} sx={{ mt: 2, mb: 1 }}>
            <CountrySelector
              name={fieldPath}
              label={translatedLabel + (field.Required ? ' *' : '')}
              onChangeCountry={(countryValue) =>
                setFieldValue(fieldPath, countryValue)
              }
              onBlur={() => handleBlur(fieldPath)}
              countries={mangopayCountryOptions}
              countryId={value}
              errorMessage={hasError ? error : undefined}
              fullWidth
              disabled={disabled}
            />
          </Box>
        );
      }

      const translatedLabel = translateFieldLabel(field.Label);
      return (
        <TextField
          key={fieldPath}
          id={fieldPath}
          name={fieldPath}
          fullWidth
          margin="normal"
          label={translatedLabel + (field.Required ? ' *' : '')}
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          error={hasError}
          helperText={hasError ? error : undefined}
          disabled={disabled}
          inputProps={{
            maxLength: field.MaxLength,
          }}
        />
      );
    } else if (typeof fieldDef === 'object') {
      // This is a nested RecipientSchema
      const nestedSchema = fieldDef as RecipientSchema;
      const nestedPath = currentPath
        ? `${currentPath}.${fieldName}`
        : fieldName;

      // Check if there are any visible fields in the nested schema
      const hasVisibleFields = Object.entries(nestedSchema).some(([_, def]) => {
        if (!def || typeof def !== 'object') {
          return false;
        }
        if (isFieldDefinition(def)) {
          return def.EndUserDisplay !== 'Hide';
        }
        // If not a FieldDefinition, it's a nested schema - check recursively
        return true;
      });

      if (!hasVisibleFields) {
        return null;
      }

      return (
        <Box key={nestedPath} sx={{ mt: 2 }}>
          <Box sx={{ fontWeight: 'medium', mb: 1 }}>
            {translateNestedFieldName(fieldName)}
          </Box>
          {Object.entries(nestedSchema).map(
            ([nestedFieldName, nestedFieldDef]) => {
              return renderField(nestedFieldName, nestedFieldDef, nestedPath);
            }
          )}
        </Box>
      );
    }

    return null;
  };

  return (
    <>
      {Object.entries(schema).map(([fieldName, fieldDef]) =>
        renderField(fieldName, fieldDef, prefix)
      )}
    </>
  );
};
